import { IsoMonth, MLSListing } from '@aloft/toolkit-shared';
import { Flex, Typography, colors } from '@aloft/toolkit-sui';

import { CartesianGrid, ComposedChart, Line, ReferenceLine } from 'recharts';

import { GraphXAxis } from '../../../components/graphs/components/GraphXAxis.tsx';
import { GraphYAxis } from '../../../components/graphs/components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../../../components/graphs/components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../../../components/graphs/components/SimpleGraphTooltip.tsx';

const formatPrice = (n: number): string => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '+';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}k`;
  return `${sign}$${abs}`;
};

type MonthDataPoint = {
  month: IsoMonth;
  avgAdjustment: number;
  interpolated: 0 | 1; // 0 = real, 1 = interpolated (bool avoided — SimpleGraphTooltip requires string | number | undefined)
  count: number;
};

// Fill every calendar month between the first and last month in the data,
// then linearly interpolate any gaps between real data points.
const fillMonthGaps = (points: MonthDataPoint[]): MonthDataPoint[] => {
  if (points.length === 0) return [];

  const sorted = [...points].sort((a, b) => a.month.localeCompare(b.month));
  const first = sorted[0]?.month;
  const last = sorted[sorted.length - 1]?.month;
  if (!first || !last) return [];

  const map = new Map(sorted.map((p) => [p.month, p]));
  const result: (MonthDataPoint & { _real: boolean })[] = [];

  const [startYear, startMonth] = first.split('-').map(Number) as [number, number];
  const [endYear, endMonth] = last.split('-').map(Number) as [number, number];

  let year = startYear;
  let month = startMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    const key = `${year}-${String(month).padStart(2, '0')}` as IsoMonth;
    const existing = map.get(key);
    result.push(
      existing
        ? { ...existing, _real: true }
        : { month: key, avgAdjustment: 0, interpolated: 0 as const, count: 0, _real: false },
    );
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }

  // Linear interpolation pass — find runs of non-real points and fill between neighbours
  for (let i = 0; i < result.length; i++) {
    const current = result[i];
    if (!current || current._real) continue;

    let lo = i - 1;
    while (lo >= 0 && !result[lo]?._real) lo--;
    let hi = i + 1;
    while (hi < result.length && !result[hi]?._real) hi++;

    if (lo < 0 || hi >= result.length) continue; // edge gap — skip

    const loPoint = result[lo];
    const hiPoint = result[hi];
    if (!loPoint || !hiPoint) continue;

    const loVal = loPoint.avgAdjustment;
    const hiVal = hiPoint.avgAdjustment;
    const steps = hi - lo;

    for (let j = lo + 1; j < hi; j++) {
      const point = result[j];
      if (!point) continue;
      const t = (j - lo) / steps;
      point.avgAdjustment = Math.round(loVal + (hiVal - loVal) * t);
      point.interpolated = 1;
    }
  }

  // Strip internal _real flag before returning
  return result.map(({ _real: _, ...p }) => p);
};

const buildDataPoints = (listings: MLSListing[]): MonthDataPoint[] => {
  const buckets = new Map<IsoMonth, number[]>();

  for (const listing of listings) {
    const { ClosePrice, AdjustedClosePrice, CloseDate } = listing;

    if (ClosePrice == null || AdjustedClosePrice == null) continue;
    if (ClosePrice <= 0 || AdjustedClosePrice <= 0) continue;

    const date = CloseDate;
    if (!date) continue;

    const month = date.substring(0, 7) as IsoMonth;
    const bucket = buckets.get(month) ?? [];
    bucket.push(AdjustedClosePrice - ClosePrice);
    buckets.set(month, bucket);
  }

  const points: MonthDataPoint[] = [];
  for (const [month, adjustments] of buckets) {
    const avg = adjustments.reduce((sum, a) => sum + a, 0) / adjustments.length;
    points.push({ month, avgAdjustment: Math.round(avg), interpolated: 0, count: adjustments.length });
  }

  return fillMonthGaps(points);
};

type RegressionAdjustmentTrendChartProps = {
  listings: MLSListing[];
};

export const RegressionAdjustmentTrendChart = ({ listings }: RegressionAdjustmentTrendChartProps) => {
  const data = buildDataPoints(listings);

  if (data.length === 0) return null;

  return (
    <Flex flexDirection="column" gap="12px">
      <Flex alignItems="center" gap="24px" flexWrap="wrap">
        <Typography variant="h4">Average Date-of-Sale Adjustment by Month</Typography>
        <Flex alignItems="center" gap="6px">
          <div style={{ width: 16, height: 2, backgroundColor: colors.graphChart }} />
          <Typography variant="body2" color="textSecondary">
            Avg adjustment
          </Typography>
        </Flex>
      </Flex>

      <ResponsiveGraphWrapper disableAnimations={false}>
        <ComposedChart
          data={data}
          margin={{ top: 16, left: 8, bottom: 8, right: 8 }}
          style={{ background: colors.graphBackground, borderRadius: 8 }}
        >
          <CartesianGrid vertical={false} />
          <GraphXAxis dataKey="month" label="Month" />
          <GraphYAxis label="Avg Adjustment" tickFormatter={(v: number) => formatPrice(v)} />
          <SimpleGraphTooltip<MonthDataPoint>
            disableAnimations={false}
            titleKey="month"
            rows={[
              {
                label: 'Avg Adjustment',
                key: 'avgAdjustment',
                color: 'graphChart',
                valueFormatter: (v) => formatPrice(v as number),
              },
              {
                label: 'Transactions',
                key: 'count',
                shouldHide: (p) => p.interpolated === 1,
              },
              {
                label: 'Note',
                key: 'interpolated',
                shouldHide: (p) => p.interpolated !== 1,
                valueFormatter: () => 'Interpolated',
              },
            ]}
          />
          <ReferenceLine y={0} stroke={colors.graphChart2} strokeWidth={1} strokeDasharray="4 3" />
          <Line
            type="monotone"
            dataKey="avgAdjustment"
            stroke={colors.graphChart}
            strokeWidth={2}
            dot={(props: any) => {
              const { cx, cy, payload } = props;
              if (payload.interpolated === 1) {
                return (
                  <circle
                    key={payload.month}
                    cx={cx}
                    cy={cy}
                    r={3}
                    fill="white"
                    stroke={colors.graphChart}
                    strokeWidth={1.5}
                    opacity={0.6}
                  />
                );
              }
              return <circle key={payload.month} cx={cx} cy={cy} r={3} fill={colors.graphChart} />;
            }}
            activeDot={{ r: 5 }}
            isAnimationActive={false}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveGraphWrapper>
    </Flex>
  );
};
